// "Sign out of this browser?" (iOS TeamSyncView sign-out confirmation, WEB_PLAN.md §4.6). Signing out first pushes
// edits that are still waiting (5 s at most); if some couldn't be saved it asks again, now offering "Try again" and
// "Sign out anyway" (which loses them), like iOS "Sync now first" / "Sign out anyway". Only this browser signs out;
// iPhones and other browsers stay signed in.

import { LogOut } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { unsavedChangesMessage } from '@/auth/accountText';
import { signOutNow } from '@/auth/AuthProvider';
import { flushPending } from '@/auth/flush';
import { SecondaryButton } from '@/features/auth/components/ui';

export function SignOutDialog({ companyName, onClose }: { companyName: string; onClose: () => void }) {
  const titleId = useId();
  const [phase, setPhase] = useState<'ask' | 'flushing' | 'unsaved' | 'signingOut'>('ask');
  const [unsaved, setUnsaved] = useState(0);
  const busy = phase === 'flushing' || phase === 'signingOut';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const signOut = async () => {
    setPhase('signingOut');
    // SIGNED_OUT wipes this browser's copy and the guards move to the sign-in screen.
    await signOutNow();
  };

  const flushThenSignOut = async () => {
    setPhase('flushing');
    const { unsaved: left } = await flushPending(5000);
    if (left > 0) {
      setUnsaved(left);
      setPhase('unsaved');
      return;
    }
    await signOut();
  };

  const message = phase === 'unsaved'
    ? unsavedChangesMessage(unsaved)
    : `${companyName}'s data is removed from this browser. Sign in again anytime to download it.`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[var(--gt-navy-deep)]/40 p-4 backdrop-blur-[2px] sm:items-center"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex w-full max-w-md flex-col gap-4 rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900"
      >
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--gt-navy)]/10 text-[var(--gt-navy)] dark:text-slate-200">
            <LogOut className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-bold text-[var(--gt-navy)] dark:text-slate-50">
              {phase === 'unsaved' ? 'Some changes aren’t saved yet' : 'Sign out of this browser?'}
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300" aria-live="polite">
              {message}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {phase === 'unsaved' || (phase === 'flushing' && unsaved > 0) ? (
            <>
              <SecondaryButton tone="navy" busy={phase === 'flushing'} busyLabel="Saving…" onClick={() => void flushThenSignOut()}>
                Try again
              </SecondaryButton>
              <SecondaryButton tone="danger" disabled={busy} onClick={() => void signOut()}>
                Sign out anyway
              </SecondaryButton>
            </>
          ) : (
            <SecondaryButton
              tone="danger"
              autoFocus
              busy={busy}
              busyLabel={phase === 'flushing' ? 'Saving changes…' : 'Signing out…'}
              onClick={() => void flushThenSignOut()}
            >
              Sign out
            </SecondaryButton>
          )}
          <SecondaryButton disabled={busy} onClick={onClose}>
            Cancel
          </SecondaryButton>
        </div>
      </div>
    </div>
  );
}
